import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CompanyContext';
import DashboardOverview from '../components/dashboard/DashboardOverview';
import AccountingQuickAccess from '../components/dashboard/AccountingQuickAccess';
import { useFinanceDrill } from '../components/reports/useFinanceDrill';
import { DataNote } from '../components/reports/DataNote';
import { 
  CurrencyDollar, 
  TrendUp, 
  TrendDown,
  Wallet,
  Bank,
  Receipt,
  Users,
  CircleNotch,
  ArrowRight,
  ShieldCheck,
  Warning,
  XCircle
} from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line
} from 'recharts';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { user } = useAuth();
  const { formatCurrency } = useCurrency();
  const { openDrill, openParty, dialogs } = useFinanceDrill();
  const [summary, setSummary] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [complianceStats, setComplianceStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [summaryRes, chartRes, invoicesRes, complianceRes] = await Promise.all([
        axios.get(`${API_URL}/dashboard/summary`),
        axios.get(`${API_URL}/dashboard/monthly-chart`),
        axios.get(`${API_URL}/dashboard/recent-invoices`),
        axios.get(`${API_URL}/compliance/stats`).catch(() => ({ data: null }))
      ]);
      setSummary(summaryRes.data);
      setChartData(chartRes.data);
      setRecentInvoices(invoicesRes.data);
      setComplianceStats(complianceRes.data);
    } catch (error) {
      // Silent fail for dashboard - may not have data yet
      console.log('Dashboard data not available yet');
    } finally {
      setLoading(false);
    }
  };

  const statusColors = {
    draft: 'bg-zinc-500/10 text-zinc-600 dark:text-zinc-400',
    approved: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
    paid: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <CircleNotch size={32} className="animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      {/* Welcome Section */}
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-1"
      >
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-semibold">
          Welcome back, {user?.first_name}
        </h1>
        <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
          Here's an overview of your business performance
        </p>
      </motion.div>

      {/* Accounting Quick Access — shortcuts to Ledger, Audit, Period Locks, etc. */}
      <AccountingQuickAccess user={user} />

      {/* Comprehensive Operational Overview */}
      <DashboardOverview />

      <DataNote />

      {/* Financial Summary Cards */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
      >
        <div className="card-tactile rounded-lg p-6 cursor-pointer hover:ring-2 hover:ring-primary/40 transition-shadow" onClick={() => openDrill({ category: 'income', title: 'Total Revenue (all time) — income entries before VAT' })} data-testid="dash-card-revenue">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-emerald-500/10">
              <TrendUp size={24} className="text-emerald-600 dark:text-emerald-400" weight="duotone" />
            </div>
            <div>
              <p className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
                Total Revenue
              </p>
              <p className="text-2xl font-heading font-semibold mt-0.5">
                {formatCurrency(summary?.total_revenue)}
              </p>
              <p className="text-[11px] text-muted-foreground">All income entries, before VAT · click for details</p>
            </div>
          </div>
        </div>

        <div className="card-tactile rounded-lg p-6 cursor-pointer hover:ring-2 hover:ring-primary/40 transition-shadow" onClick={() => openDrill({ category: 'expense', title: 'Total Expenses (all time) — expense entries' })} data-testid="dash-card-expenses">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-red-500/10">
              <TrendDown size={24} className="text-red-600 dark:text-red-400" weight="duotone" />
            </div>
            <div>
              <p className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
                Total Expenses
              </p>
              <p className="text-2xl font-heading font-semibold mt-0.5">
                {formatCurrency(summary?.total_expenses)}
              </p>
              <p className="text-[11px] text-muted-foreground">All expense entries, before VAT · click for details</p>
            </div>
          </div>
        </div>

        <div className="card-tactile rounded-lg p-6 cursor-pointer hover:ring-2 hover:ring-primary/40 transition-shadow" onClick={() => openParty('customer')} data-testid="dash-card-ar">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-blue-500/10">
              <Wallet size={24} className="text-blue-600 dark:text-blue-400" weight="duotone" />
            </div>
            <div>
              <p className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
                Accounts Receivable
              </p>
              <p className="text-2xl font-heading font-semibold mt-0.5">
                {formatCurrency(summary?.accounts_receivable)}
              </p>
              <p className="text-[11px] text-muted-foreground">Unpaid invoice balances · click for details</p>
            </div>
          </div>
        </div>

        <div className="card-tactile rounded-lg p-6 cursor-pointer hover:ring-2 hover:ring-primary/40 transition-shadow" onClick={() => openParty('supplier')} data-testid="dash-card-ap">
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-lg bg-amber-500/10">
              <Bank size={24} className="text-amber-600 dark:text-amber-400" weight="duotone" />
            </div>
            <div>
              <p className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
                Accounts Payable
              </p>
              <p className="text-2xl font-heading font-semibold mt-0.5">
                {formatCurrency(summary?.accounts_payable)}
              </p>
              <p className="text-[11px] text-muted-foreground">Unpaid approved bills · click for details</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Net Income Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.15 }}
        className="card-tactile rounded-lg p-6 cursor-pointer hover:ring-2 hover:ring-primary/40 transition-shadow"
        onClick={() => openDrill({ category: 'pnl', title: 'Net Income (all time) — revenue less expenses' })}
        data-testid="dash-card-net-income"
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs tracking-[0.05em] uppercase font-bold text-muted-foreground">
              Net Income
            </p>
            <p className={`text-3xl font-heading font-semibold mt-1 ${(summary?.net_income || 0) >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
              {formatCurrency(summary?.net_income)}
            </p>
          </div>
          <div className="flex gap-8">
            <div className="text-center">
              <p className="text-xs text-muted-foreground">Invoices</p>
              <p className="text-xl font-semibold">{summary?.total_invoices || 0}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground">Pending</p>
              <p className="text-xl font-semibold text-amber-600">{summary?.pending_invoices || 0}</p>
            </div>
            <div className="text-center">
              <p className="text-xs text-muted-foreground">Customers</p>
              <p className="text-xl font-semibold">{summary?.total_customers || 0}</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Charts and Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Monthly Income vs Expense Chart */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="lg:col-span-2 card-tactile rounded-lg p-6"
        >
          <h3 className="font-heading text-lg font-medium mb-4">
            Monthly Income vs Expenses ({chartData?.year || new Date().getFullYear()})
          </h3>
          <div className="h-[300px]">
            {chartData?.data && chartData.data.some(d => d.income > 0 || d.expense > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData.data}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                  <XAxis dataKey="month_name" className="text-xs" />
                  <YAxis className="text-xs" tickFormatter={(v) => `$${v}`} />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    formatter={(value) => [`$${value.toFixed(2)}`, '']}
                  />
                  <Legend />
                  <Bar dataKey="income" name="Income" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="expense" name="Expense" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-muted-foreground">
                <div className="text-center">
                  <CurrencyDollar size={48} weight="duotone" className="mx-auto mb-2 opacity-50" />
                  <p>No transaction data yet</p>
                  <p className="text-sm">Create invoices and record expenses to see the chart</p>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Recent Invoices */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="card-tactile rounded-lg p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-medium">Recent Invoices</h3>
            <Link to="/dashboard/sales" className="text-sm text-primary hover:underline flex items-center gap-1">
              View all <ArrowRight size={14} />
            </Link>
          </div>
          <div className="space-y-3">
            {recentInvoices.length > 0 ? (
              recentInvoices.map((invoice) => (
                <div key={invoice.id} className="flex items-center justify-between p-3 rounded-lg bg-muted/30">
                  <div>
                    <p className="font-medium text-sm">{invoice.customer_name}</p>
                    <p className="text-xs text-muted-foreground">{invoice.document_number}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono text-sm">{formatCurrency(invoice.total)}</p>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[invoice.status] || 'bg-muted'}`}>
                      {invoice.status}
                    </span>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center py-8 text-muted-foreground">
                <Receipt size={32} weight="duotone" className="mx-auto mb-2 opacity-50" />
                <p className="text-sm">No invoices yet</p>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Compliance Alerts Widget */}
      {complianceStats && (complianceStats.expiring_soon > 0 || complianceStats.expired > 0) && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.28 }}
          className="card-tactile rounded-lg p-5 border-l-4 border-amber-500"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <ShieldCheck size={22} className="text-primary" weight="duotone" />
              <h3 className="font-heading text-lg font-medium">Compliance Alerts</h3>
            </div>
            <Link to="/dashboard/compliance" className="text-sm text-primary hover:underline flex items-center gap-1">
              View all <ArrowRight size={14} />
            </Link>
          </div>
          
          <div className="flex flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/10 text-emerald-600">
              <ShieldCheck size={16} />
              <span className="text-sm font-medium">{complianceStats.active} Active</span>
            </div>
            {complianceStats.expiring_soon > 0 && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 text-amber-600">
                <Warning size={16} />
                <span className="text-sm font-medium">{complianceStats.expiring_soon} Expiring Soon</span>
              </div>
            )}
            {complianceStats.expired > 0 && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/10 text-red-600">
                <XCircle size={16} />
                <span className="text-sm font-medium">{complianceStats.expired} Expired</span>
              </div>
            )}
          </div>
          
          {complianceStats.upcoming_expirations?.length > 0 && (
            <div className="space-y-2">
              {complianceStats.upcoming_expirations.slice(0, 3).map(item => (
                <div key={item.id} className="flex items-center justify-between p-2 rounded-lg bg-muted/30">
                  <div>
                    <p className="text-sm font-medium">{item.title}</p>
                    <p className="text-xs text-muted-foreground">{item.entity_name}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    item.status === 'expired' 
                      ? 'bg-red-500/10 text-red-600' 
                      : 'bg-amber-500/10 text-amber-600'
                  }`}>
                    {item.days_until_expiry < 0 ? 'Expired' : `${item.days_until_expiry}d left`}
                  </span>
                </div>
              ))}
            </div>
          )}
        </motion.div>
      )}

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="grid grid-cols-2 sm:grid-cols-4 gap-4"
      >
        <Link to="/dashboard/contacts" className="card-tactile rounded-lg p-4 text-center hover:border-primary/50 transition-colors">
          <Users size={28} className="mx-auto mb-2 text-blue-600 dark:text-blue-400" weight="duotone" />
          <p className="text-sm font-medium">Contacts</p>
        </Link>
        <Link to="/dashboard/sales" className="card-tactile rounded-lg p-4 text-center hover:border-primary/50 transition-colors">
          <Receipt size={28} className="mx-auto mb-2 text-emerald-600 dark:text-emerald-400" weight="duotone" />
          <p className="text-sm font-medium">Invoices</p>
        </Link>
        <Link to="/dashboard/accounts" className="card-tactile rounded-lg p-4 text-center hover:border-primary/50 transition-colors">
          <CurrencyDollar size={28} className="mx-auto mb-2 text-amber-600 dark:text-amber-400" weight="duotone" />
          <p className="text-sm font-medium">Accounts</p>
        </Link>
        <Link to="/dashboard/accounts/trial-balance" className="card-tactile rounded-lg p-4 text-center hover:border-primary/50 transition-colors">
          <Bank size={28} className="mx-auto mb-2 text-purple-600 dark:text-purple-400" weight="duotone" />
          <p className="text-sm font-medium">Trial Balance</p>
        </Link>
      </motion.div>
      {dialogs}
    </div>
  );
}
